import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as UpgradeIcon,
  TrendingDown as DowngradeIcon,
  Whatshot as FireIcon
} from '@mui/icons-material';
import {
  getPlanDisplayName,
  getPlanDescription,
  isPlanPopular,
  PLAN_RELATIONSHIP,
} from '../utils/planUtils';
import { buildPlanFeatures, planCredits } from '../utils/planFeatures';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import PlanFeatureList from './PlanFeatureList';

// ── Plan-button palette ──────────────────────────────────────────────────────
// Reuses colours already established elsewhere in the app rather than inventing
// new ones: ORANGE is the CTA accent used across the dashboard, GREEN is the
// same tone the "Active" website status badge uses (Dashboard C.activeStatus),
// and the greys are CreditManagement's muted text/border pair.
const ORANGE = '#FF7A59';
const ORANGE_HOVER = '#FF5722';
const GREEN = '#006A63';
const GREEN_TINT = 'rgba(0,106,99,0.06)';
const GREY_TEXT = '#8A8F85';
const GREY_BORDER = '#DDE0D8';

const PlanCard = ({
  plan,
  isCurrentPlan,
  onSelectPlan,
  isPending = false,
  disabled = false,
  // 'month' | 'year'. Which price this card shows and which one the button
  // buys — they must be the same value, so it is one prop rather than two.
  interval = 'month',
  // How this plan relates to the one the user is on. Drives colour + label.
  // Defaults to UPGRADE so a caller that hasn't been updated still renders the
  // pre-existing orange "Upgrade" treatment rather than something broken.
  relationship = PLAN_RELATIONSHIP.UPGRADE,
  /**
   * True when the account has no paid plan running — a free trial, or the
   * Basic plan — so choosing a plan STARTS a subscription rather than moving
   * between two. The button then reads "Subscribe" ("Subscribe now" on the
   * popular plan) instead of "Upgrade": there is no plan to upgrade FROM.
   *
   * Label only. Colour, icon and the most-popular emphasis still follow
   * `relationship`, which PlanSelector sets to UPGRADE for every card here.
   */
  subscribing = false,
  /**
   * Opens the cancel-subscription dialog, from under the Current Plan button.
   *
   * Optional, and PASSED ONLY FOR THE CARD THE USER IS ON — see PlanSelector,
   * which also decides whether cancelling is offered at all (it is not, once a
   * cancellation is already scheduled). A card without it renders no Cancel
   * button, which is every card on the screen but one.
   */
  onCancelPlan = undefined,
}) => {
  const displayName = getPlanDisplayName(plan);
  const description = getPlanDescription(plan);

  const isCurrent = relationship === PLAN_RELATIONSHIP.CURRENT || isCurrentPlan === true;
  const isDowngrade = relationship === PLAN_RELATIONSHIP.DOWNGRADE;
  const isUpgrade = !isCurrent && !isDowngrade;

  // The "most popular" treatment is a nudge to BUY. It only makes sense while
  // the plan is still something to buy: once the user is on Optimize (scenario
  // 3) or has moved past it to Scale (scenario 4), the spec shows that card as
  // a plain Current Plan / Downgrade, with no orange and no emphasis. Gating on
  // isUpgrade is what makes scenarios 3 and 4 come out right — previously
  // isPlanPopular() alone kept Optimize filled orange and scaled up in every
  // scenario, including when it was the user's current plan.
  const isPopular = isPlanPopular(plan) && isUpgrade;

  // Only show a yearly price when there IS one. A plan without an annual price
  // configured stays on monthly rather than showing "$NaN/Year".
  const showYearly = interval === 'year' && plan.price_cents_yearly != null;

  // ── The feature list ──────────────────────────────────────────────────────
  // Built from the plan rather than hardcoded here. The previous version opened
  // with three rows — "220 SEO Content Credits / 220 AI Image Credits / 220
  // Keyword List Credits" — that were the SAME 220: plan-credit-sync writes the
  // one ISEO grant into all three legacy columns, so a single pool was
  // advertised as though it were three allowances totalling 660.
  //
  // See utils/planFeatures.js for the rest, including why the website count is
  // read from the column the server enforces rather than printed.
  // showYearly, not the raw interval: it is the price THIS card is showing,
  // and the cancellation line has to describe that rather than the state of
  // the toggle above it.
  // "≈ N pieces" under the credit figure, from GET /credits/estimate; the line
  // is omitted until it answers.
  const pieces = usePieceEstimate(planCredits(plan));
  const features = buildPlanFeatures(plan, { pieces, yearly: showYearly });

  // OLD:
  //   if (isCurrentPlan) return 'Current';
  //   if (isPopular) return 'Upgrade Now';
  //   return 'Upgrade';
  const getButtonText = () => {
    if (isCurrent) return 'Current Plan';
    // ── The plan a downgrade is already scheduled to ──────────────────────
    // "Downgrade" invited an action that had already been taken, and taking it
    // again was accepted in full: a second quote, a second disclosure row, a
    // redundant Stripe update, and a success dialog congratulating the customer
    // on scheduling what was already scheduled. The button now states the
    // state. It stays CLICKABLE on purpose — pressing it explains the schedule
    // (see PlanSelector's reminder dialog), which a disabled control cannot do.
    if (isPending) return 'Downgrade scheduled';
    if (isDowngrade) return 'Downgrade';
    // No paid plan running (a trial, or Basic): the click STARTS a
    // subscription, and "Upgrade" names a plan to move up from that the
    // account does not have. See the `subscribing` prop.
    if (subscribing) return isPopular ? 'Subscribe now' : 'Subscribe';
    return isPopular ? 'Upgrade Now' : 'Upgrade';
  };

  const getButtonIcon = () => {
    if (isCurrent) return <CheckIcon />;
    // Scheduled and not-yet-scheduled downgrades share an icon: the direction
    // is the same, and only the label distinguishes what is left to do.
    if (isPending || isDowngrade) return <DowngradeIcon />;
    return isPopular ? <FireIcon /> : <UpgradeIcon />;
  };

  // White button, coloured text + outline — the shared shape for every state
  // except the emphasised Optimize upgrade, which is filled orange.
  const outlinedIn = (color, borderColor = color, hoverTint = 'rgba(255, 122, 89, 0.04)') => ({
    bgcolor: '#FFFFFF',
    borderColor,
    color,
    '&:hover': { borderColor, bgcolor: hoverTint },
  });

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        // Card outline follows the button: orange while the popular plan is
        // still an upgrade, green once it's the plan you're on, otherwise
        // neutral. Without the green the current card would be the plainest one
        // on screen, which reads as disabled rather than "this is yours".
        border: isPopular ? `2px solid ${ORANGE}` : isCurrent ? `2px solid ${GREEN}` : '1px solid',
        borderColor: isPopular ? ORANGE : isCurrent ? GREEN : '#eee',
        position: 'relative',
        overflow: 'visible',
        boxShadow: isPopular
          ? '0 8px 32px rgba(255, 122, 89, 0.18)'
          : '0 2px 12px rgba(0,0,0,0.06)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: isPopular ? 'scale(1.03)' : 'none',
        // Scaled from the TOP edge, so the emphasis grows downwards and the
        // popular card still starts on the same line as the other two. A
        // default (centre) origin spreads the extra 3% evenly above and below,
        // which lifts this one card clear of the row the grid just aligned —
        // about 13px on a card this tall, and the only thing left breaking the
        // top line. The extra length lands at the bottom, where the cards are
        // allowed to differ anyway.
        transformOrigin: 'top center',
        zIndex: isPopular ? 1 : 0,
        '&:hover': {
          transform: isPopular ? 'scale(1.04)' : 'translateY(-4px)',
          boxShadow: isPopular
            ? '0 12px 40px rgba(255, 122, 89, 0.25)'
            : '0 6px 24px rgba(0,0,0,0.1)',
        },
      }}
    >
      {/* MOST POPULAR badge — positioned on the top border */}
      {isPopular && (
        <Chip
          label="MOST POPULAR"
          sx={{
            position: 'absolute',
            top: -14,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: 0.5,
            bgcolor: '#1C7C73',
            color: '#fff',
            height: 28,
            px: 1,
          }}
        />
      )}

      {/* Pending downgrade badge */}
      {isPending && (
        <Chip
          label="Pending Downgrade"
          color="warning"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 700,
            fontSize: '0.65rem',
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1, p: 3, pt: isPopular || isPending ? 4.5 : 3 }}>
        {/* Plan Name */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {displayName}
        </Typography>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, minHeight: 40 }}>
          {description}
        </Typography>

        {/* Price — yearly when the toggle asks for it AND this plan has an
            annual price configured. Falling back to the monthly figure rather
            than rendering a blank keeps a partially-configured plan sellable. */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="span"
            sx={{ fontSize: '2.2rem', fontWeight: 700, color: '#FF7A59' }}
          >
            ${((showYearly ? plan.price_cents_yearly : plan.price_cents) / 100).toFixed(2)}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: '0.95rem', color: 'text.secondary', ml: 0.3 }}
          >
            {showYearly ? '/Year' : '/Month'}
          </Typography>
          {/* The saving is stated in money, not just as a percentage: "$19.00 a
              year" is a number people can act on, and it comes from the server
              so it always matches what is actually charged. */}
          {showYearly && plan.yearly_saving_cents > 0 && (
            <Typography
              sx={{ fontSize: '0.8rem', fontWeight: 700, color: GREEN, mt: 0.5 }}
            >
              Save ${(plan.yearly_saving_cents / 100).toFixed(2)} a year
            </Typography>
          )}
        </Box>

        {/* Features — see utils/planFeatures.js. Rows are objects rather than
            strings because two of them carry a second level: the credit row has
            a muted piece estimate beneath it, and the flexibility row has
            indented bullets. */}
        <PlanFeatureList features={features} />
      </CardContent>

      {/* Button */}
      <Box sx={{ p: 3, pt: 0 }}>
        {/* Button colour is derived from `relationship`, so the four scenarios
            in the spec are one rule rather than four hand-styled cases:
              CURRENT   → white, green text + outline   (not clickable)
              UPGRADE   → white, orange text + outline  … except the popular
                          plan, which is filled orange as the primary CTA
              DOWNGRADE → white, soft-grey text + outline

            OLD: colour keyed off isPopular/isCurrentPlan only, so a downgrade
            was indistinguishable from an upgrade (both orange), and Optimize
            stayed filled orange even when it was the user's current plan. */}
        <Button
          fullWidth
          size="large"
          variant={isPopular ? 'contained' : 'outlined'}
          // The current plan is a state, not an action. Kept disabled so it
          // can't be "bought" again, with explicit Mui-disabled styling below —
          // otherwise MUI greys it out and the green disappears.
          disabled={isCurrent || disabled}
          onClick={() => onSelectPlan(plan)}
          startIcon={getButtonIcon()}
          sx={{
            py: 1.4,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '0.95rem',
            textTransform: 'none',
            ...(isPopular
              ? {
                  background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)`,
                  boxShadow: '0 4px 14px rgba(255, 87, 34, 0.35)',
                  color: '#fff',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${ORANGE_HOVER} 0%, #E64A19 100%)`,
                    boxShadow: '0 6px 20px rgba(255, 87, 34, 0.45)',
                  },
                }
              : outlinedIn(ORANGE, ORANGE, 'rgba(255, 122, 89, 0.04)')),

            // Downgrade — deliberately the quietest treatment on the card. It
            // is a real action, just never the one being encouraged.
            ...(isDowngrade && outlinedIn(GREY_TEXT, GREY_BORDER, 'rgba(0,0,0,0.03)')),

            // Current plan — green, and it must survive MUI's disabled styling.
            ...(isCurrent && {
              ...outlinedIn(GREEN, GREEN, GREEN_TINT),
              boxShadow: 'none',
              '&.Mui-disabled': {
                bgcolor: '#FFFFFF',
                borderColor: GREEN,
                color: GREEN,
                // Slightly muted so it still reads as non-interactive, while
                // staying clearly green rather than MUI's default grey.
                opacity: 0.9,
              },
            }),
          }}
        >
          {getButtonText()}
        </Button>

        {/* ── Cancel, under the plan the user is actually on ────────────────
            Cancelling was reachable only from the Credits page, so someone who
            had navigated to Manage Plan to reconsider their subscription had to
            go BACK to find it — on the one screen dedicated to changing what
            you pay for.

            Quieter than every other control on the card, by design: white
            ground, grey label, grey outline — the same treatment Downgrade
            gets, which is the card's existing vocabulary for "a real action,
            just never the one being encouraged". It must not compete with the
            upgrade CTAs, and it sits below the primary button so it reads as a
            footnote to the current plan rather than a fourth option.

            `disabled` is deliberately NOT applied from the card's own
            disabled prop alone — see the button above, which is disabled
            precisely BECAUSE this is the current plan. Cancelling stays
            available in that state; it is suppressed only while another plan
            action is mid-flight. */}
        {isCurrent && onCancelPlan && (
          <Button
            fullWidth
            size="medium"
            variant="outlined"
            onClick={onCancelPlan}
            disabled={disabled}
            sx={{
              mt: 1.25,
              py: 1,
              borderRadius: 2.5,
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              boxShadow: 'none',
              ...outlinedIn(GREY_TEXT, GREY_BORDER, 'rgba(0,0,0,0.03)'),
            }}
          >
            Cancel Subscription
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default PlanCard;
